<template>
  <section class="container-fluid" data-testid="paypal-card-form">
    <div class="row">
      <div class="col-xs-12">
        <form @submit.prevent="submit()" novalidate>
          <fieldset>
            <section style="margin-bottom: 2em;" v-if="includeBillingAddress">
              <h3>Billing Address</h3>
              <fieldset>
                <div class="form-group row">
                  <div class="col-sm-12">
                    <label for="billing_address_street">Street</label>
                    <input
                      class="form-control"
                      v-model="billingAddress.street"
                      id="billing_address_street"
                    />
                  </div>
                </div>
                <div class="form-group row">
                  <div class="col-sm-12 col-md-4">
                    <label for="billing_address_city">City</label>
                    <input
                      class="form-control"
                      v-model="billingAddress.city"
                      id="billing_address_city"
                    />
                  </div>
                  <div class="col-sm-12 col-md-4">
                    <label for="billing_address_state">State</label>
                    <select
                      class="form-control"
                      v-model="billingAddress.state"
                      id="billing_address_state"
                    >
                      <option
                        v-for="(opt, $index) in states"
                        :key="$index"
                        :value="opt.value"
                      >
                        {{ opt.name }}
                      </option>
                  </select>

                  </div>
                  <div class="col-sm-12 col-md-4">
                    <label for="billing_address_zip">Zip/Postal Code</label>
                    <input
                      class="form-control"
                      v-model="billingAddress.zip"
                      id="billing_address_zip"
                    />
                  </div>
                </div>
              </fieldset>
            </section>

            <h3 class="mb-4">Card Information</h3>
            
            <!-- PayPal Card Fields -->
            <div class="form-group">
              <label for="card_name_field">Name on Card</label>
              <section id="card_name_field"></section>
            </div>

            <div class="form-group">
              <label for="card_number_field">Card Number</label>
              <section id="card_number_field"></section>
            </div>

            
            <div class="form-group">
              <label for="card_expiry_field">Expiration Date</label>
              <section id="card_expiry_field"></section>
            </div>
            <div class="form-group">
              <label for="card_cvv_field">CVC</label>
              <section id="card_cvv_field"></section>
            </div>

          </fieldset>

          <div class="row" style="margin-bottom: 2em;" v-if="tokenizationSupported === true">
              <div class="col-sm-12">
                  <fieldset>
                      <label class="checkbox" for="save_payment_method">
                          <input type="checkbox"
                              v-model="payment.save_payment_method"
                              id="save_payment_method">
                          <span class="outer">
                              <span class="inner"></span>
                          </span>
                          Save this payment method
                      </label>
                  </fieldset>
              </div>
          </div>

          <div class="text-center" style="margin-bottom: 2em">
            <button
              class="btn btn-dc"
              :disabled="buttonDisabled"
              style="margin-top: 2em"
            >
              {{ buttonText }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { States } from "../../constants/states";
import { loadScript } from "@paypal/paypal-js";

export default {
  props: {
    clientId: { type: String, default: "" },
    billingAddress: {
      type: Object,
      default: function () {
        return {
          street: "",
          state: "",
          city: "",
          zip: "",
          country: "US",
        };
      },
    },
    includeBillingAddress: {
      type: Boolean,
      default: false,
    },
    tokenizationSupported: { type: Boolean, default: false },
    buttonDisabled: { type: Boolean, default: false },
    buttonText: { type: String, default: "Submit" },
    createOrder: { type: Function, default: () => {} },
    beforeSubmit: { type: Function, async default() { return {}; } },
    // When true, skip calling CAPTURE_PAYPAL_ORDER in onApprove (parent handles capture)
    skipCaptureOnApprove: { type: Boolean, default: false },
  },
  data() {
    return {
      billingAddressAsHome: false,
      states: States,
      paypal: null,
      paypalLoaded: false,
      cardFields: null,
      nameField: null,
      numberField: null,
      expiryField: null,
      cvvField: null,
      payment: {
        save_payment_method: false,
      },
      // Store the DriveScout order ID for use in capture call
      drivescoutOrderId: null,
    };
  },
  async mounted() {
    this.paypal = await loadScript({
      "client-id": this.clientId,
      components: ["card-fields"],
    });
    this.setupPayPal();
  },
  beforeDestroy() {
    // Clean up PayPal card fields to prevent scroll issues after navigation
    // Each field creates an iframe that must be explicitly closed
    if (this.nameField) {
      this.nameField.close();
    }
    if (this.numberField) {
      this.numberField.close();
    }
    if (this.expiryField) {
      this.expiryField.close();
    }
    if (this.cvvField) {
      this.cvvField.close();
    }
  },
  methods: {
    setupPayPal() {
      if (!this.paypal || !this.paypal.CardFields) {
        console.error('PayPal Card Fields not available');
        return;
      }

      this.paypalLoaded = true;

      // Create the card fields component
      this.cardFields = this.paypal.CardFields({
        style: {
          'input': {
            'font-size': '16px',
            'font-family': 'helvetica, tahoma, calibri, sans-serif',
            'color': '#3a3a3a'
          },
          ':focus': {
            'color': 'black'
          },
          '.invalid': {
            'color': 'red'
          }
        },
        createOrder: async (data, actions) => {
          // Call beforeSubmit to store billing address data before creating order
          // Pass the billing address from this component to the parent
          await this.beforeSubmit(this.billingAddress);

          // Call the parent's createOrder which returns both order IDs
          const orderResult = await this.createOrder(data, actions);

          // Store DriveScout order ID for use in capture call
          this.drivescoutOrderId = orderResult.drivescout_order_id;

          // Return PayPal order ID to PayPal SDK
          return orderResult.paypal_order_id;
        },
        onApprove: async (data, actions) => {
          try {
            // For existing order payments, skip capture here - parent handles it
            if (!this.skipCaptureOnApprove) {
              // Confirm the order with our backend (for NEW orders only)
              // Send PayPal order ID and payment token (use orderID as token for Card Fields)
              await this.$store.dispatch('payments/CAPTURE_PAYPAL_ORDER', {
                order_id: this.drivescoutOrderId,
                paypal_order_id: data.orderID,
                payment_token: data.orderID, // For Card Fields, the order ID serves as the payment token
              });
            }

            // Emit the captured order data in the format expected by the controller
            this.tokenCreated({
              paypal_order_id: data.orderID,
              drivescout_order_id: this.drivescoutOrderId,
              save_payment_method: this.payment.save_payment_method
            });
          } catch (error) {
            console.error('Failed to confirm PayPal order:', error);
            this.$emit('error', error);
          }
        },
        onError: (err) => {
          console.error('PayPal Card Fields error:', err);
          this.$emit('error', err);
        },
        inputEvents: {
          onChange: (data) => {
            // Handle field changes
            // console.log('Card field changed:', data);
          },
          onFocus: (data) => {
            // Handle field focus
            // console.log('Card field focused:', data);
          },
          onBlur: (data) => {
            // Handle field blur
            // console.log('Card field blurred:', data);
          },
          onInputSubmitRequest: (data) => {
            // Handle submit request from field
            console.log('Card field input submit request:', data);
            this.submit();
          }
        }
      });

      // Check if card fields are eligible
      
      if (this.cardFields.isEligible()) {
        // Render individual fields
        this.nameField = this.cardFields.NameField();
        this.nameField.render('#card_name_field');

        this.numberField = this.cardFields.NumberField();
        this.numberField.render('#card_number_field');

        this.expiryField = this.cardFields.ExpiryField();
        this.expiryField.render('#card_expiry_field');

        this.cvvField = this.cardFields.CVVField();
        this.cvvField.render('#card_cvv_field');
      } else {
        console.error('PayPal Card Fields not eligible');
      }
      
    },

    async submit() {
      if (!this.cardFields) {
        console.error('PayPal Card Fields not initialized');
        return;
      }

      try {
        // Submit the card fields
        const result = await this.cardFields.submit();
        
        // The onApprove callback will be triggered automatically
        console.log('Card fields submitted successfully:', result);

      } catch (err) {
        console.error('PayPal submission error:', err);
        this.$emit('error', err);
      }
    },

    setBillingAddressAsHome(v) {
      this.$emit("setBillingAddressAsHome");
    },

    tokenCreated(data) {
      this.$emit("paypalCardTokenCreated", data);
    },

    pay() {
      this.submit();
    },
  },
};
</script>

<style scoped>
/* PayPal Card Fields styling */
#card_name_field,
#card_number_field,
#card_expiry_field,
#card_cvv_field {
  /*
  min-height: 40px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 12px;
  background-color: white;
  */
}

#card_name_field:focus,
#card_number_field:focus,
#card_expiry_field:focus,
#card_cvv_field:focus {
  /*
  border-color: #007cba;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 124, 186, 0.2);
  */
}
</style>
